// Severity-aware drop-in replacement for sonner's `toast`.
//
// The app gates toasts behind TOASTS_ENABLED (dev-only by default), which made
// every `toast.error(...)` a SILENT NO-OP in production — users got no feedback
// when a save/publish/permission action failed. Failure feedback is not noise.
//
// This wrapper keeps informational toasts gated (success / info / message /
// loading / bare `toast(...)`) while letting `error` and `warning` ALWAYS
// render. Import `toast` from "@/lib/toast" instead of "sonner" everywhere; the
// <Toaster/> is now mounted unconditionally in app/providers.tsx.

import { toast as baseToast } from "sonner";
import { TOASTS_ENABLED } from "./config";

// Failure severities that must reach the user even in production.
const ALWAYS_SHOWN = new Set<PropertyKey>(["error", "warning"]);
// Control / lifecycle helpers that aren't a plain informational toast.
const PASS_THROUGH = new Set<PropertyKey>(["dismiss", "promise", "custom"]);

export const toast = new Proxy(baseToast, {
  // Bare `toast("…")` is an info-level message → gated.
  apply(target, thisArg, args) {
    return TOASTS_ENABLED ? Reflect.apply(target as (...a: unknown[]) => unknown, thisArg, args) : undefined;
  },
  get(target, prop, receiver) {
    const value = Reflect.get(target, prop, receiver);
    if (typeof value !== "function") return value;
    if (ALWAYS_SHOWN.has(prop) || PASS_THROUGH.has(prop) || TOASTS_ENABLED) {
      return (value as (...a: unknown[]) => unknown).bind(target);
    }
    // Gated informational variant in production: no-op that still returns
    // undefined so callers threading a toast id into a later update don't break.
    return () => undefined;
  },
}) as typeof baseToast;

export const notify = baseToast;
